import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Plus } from 'lucide-react'
import {
  Card, EmptyState, Loading, SearchInput, Select, StatusPill, Th, sortRows, useSort,
} from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { usePrs, useSites } from '@/lib/data'
import { can, seesManySites, visibleSites } from '@/lib/domain'
import { ddmmyyyy, rupees } from '@/lib/format'

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Awaiting approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'PARTIALLY_FULFILLED', label: 'Partially fulfilled' },
  { value: 'FULFILLED', label: 'Fulfilled' },
  { value: 'RETURNED', label: 'Returned for revision' },
  { value: 'REJECTED', label: 'Rejected' },
]

export function PrList() {
  const { profile } = useAuth()
  const role = profile!.role
  const ho = seesManySites(role)
  // A Project Manager picking a colleague's site would see nothing, which
  // looks like a fault rather than a boundary. Offer him only his own.
  const [siteFilter, setSiteFilter] = useState(ho ? '' : (profile!.site_id ?? ''))
  const [status, setStatus] = useState('')
  const [type, setType] = useState('')
  const [term, setTerm] = useState('')

  const { data: sites } = useSites()
  const mySites = useMemo(() => visibleSites(sites, role, profile!.id), [sites, role, profile])
  const { data, loading, error } = usePrs({
    site_id: siteFilter || undefined,
    status: status ? [status] : undefined,
    pr_type: type || undefined,
  })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((p) =>
      p.id.toLowerCase().includes(t) ||
      (p.remarks ?? '').toLowerCase().includes(t) ||
      (p.creator_email ?? '').toLowerCase().includes(t))
  }, [data, term])


  const { sort, toggle } = useSort('date_created')
  const sorted = useMemo(() => sortRows(rows, sort, {
    date_created: 'date', required_by_date: 'date', total_estimated_amount: 'number',
  }), [rows, sort])

  return (
    <div className="space-y-4">
      <Card
        title="Purchase Requisitions"
        subtitle="Raised by the site store-man, then Head of Stores → Project Manager → Project Head"
        actions={can(role, 'createPr') && (
          <Link to="/prs/new" className="btn-primary btn-sm"><Plus className="h-4 w-4" /> New requisition</Link>
        )}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <SearchInput value={term} onChange={setTerm} placeholder="Search PR number, remarks, creator…" />
          {ho && (
            <Select
              value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}
              placeholder="All sites"
              options={mySites.map((s) => ({ value: s.id, label: s.site_name }))}
            />
          )}
          <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_FILTERS} />
          <Select
            value={type} onChange={(e) => setType(e.target.value)}
            placeholder="All types"
            options={[
              { value: 'STANDARD', label: 'Standard (for procurement)' },
              { value: 'IST', label: 'IST (internal transfer)' },
            ]}
          />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load requisitions" message={error} /></div>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState
            icon={<ClipboardList className="h-9 w-9" />}
            title="No requisitions match"
            message="Adjust the filters, or raise a new requisition."
            action={can(role, 'createPr') && <Link to="/prs/new" className="btn-primary btn-sm">New requisition</Link>}
          />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>PR number</Th><Th id="site_id" sort={sort} onSort={toggle}>Site</Th><Th id="pr_type" sort={sort} onSort={toggle}>Type</Th><Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <Th id="urgency" sort={sort} onSort={toggle}>Urgency</Th><Th id="date_created" sort={sort} onSort={toggle}>Raised</Th><Th id="required_by_date" sort={sort} onSort={toggle}>Needed by</Th>
                  <Th id="total_estimated_amount" sort={sort} onSort={toggle} className="num" align="right">Estimated</Th><Th id="remarks" sort={sort} onSort={toggle}>Remarks</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/prs/${p.id}`} className="font-mono text-xs font-semibold text-oval-700 hover:underline">
                        {p.id}
                      </Link>
                      {p.is_legacy && <span className="pill-grey ml-1.5">migrated</span>}
                    </td>
                    <td className="text-xs">{p.site_id}</td>
                    <td>{p.pr_type === 'IST' ? <span className="pill-violet">IST</span> : <span className="pill-grey">Standard</span>}</td>
                    <td><StatusPill status={p.status} /></td>
                    <td className="text-xs">{p.urgency}</td>
                    <td className="text-xs">{ddmmyyyy(p.date_created)}</td>
                    <td className="text-xs">{ddmmyyyy(p.required_by_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(p.total_estimated_amount)}</td>
                    <td className="max-w-[220px] truncate text-xs text-slate-500">{p.remarks ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
